import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Fade
} from "@material-ui/core";
import { color, fontFamily, fontSize } from "../../style/constants";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";

interface RecordingConsentScreenProps {
  onConsent: (allowed: boolean) => void;
}

export const RecordingConsentScreen: React.FC<RecordingConsentScreenProps> = ({
  onConsent
}) => {
  const [page, setPage] = useState(0);

  const pages = [
    {
      title: "May we record this coaching session for training purposes?",
      content:
        "Read through these slides for more information, and you can select YES or NO.",
      icon: <RecordVoiceOverIcon style={{ fontSize: 48, color: color.BLUE }} />
    },
    {
      title: "What's the purpose of recording?",
      content:
        "The purpose of recording the session is to help our team train, monitor, and equip coaches to be as effective as possible. Before each session, you can accept or decline the recording option."
    },
    {
      title: "How is this recording being used and protected?",
      content:
        "Only certified and approved research personnel can access this recording. Similar to other data in the platform, this recording will be stored digitally in a centralized database that is being hosted on Amazon Web Services."
    },
    {
      title: "May we record this coaching session for training purposes?",
      content: "This decision will not impact your coaching call"
    }
  ];

  return (
    <Box className="zoom-app-wrapper">
      <Card elevation={3} className="zoom-app-card">
        <CardContent className="zoom-app-content">
          <LinearProgress
            variant="determinate"
            value={(page / (pages.length - 1)) * 100}
            style={{
              height: 4,
              borderRadius: 4,
              backgroundColor: "rgb(3, 155, 229)",
              marginTop: 10,
              marginBottom: 20
            }}
          />
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            p={3}
            height="calc(100% - 28px)"
            justifyContent="center"
          >
            <Box mb={2}>
              <img
                src={process.env.REACT_APP_COACHING_GROUP_CALL}
                alt="Logo"
                style={{
                  maxHeight: 180,
                  maxWidth: 320,
                  width: "auto",
                  height: "auto",
                  objectFit: "contain"
                }}
              />
            </Box>
            <Box width="100%" mb={4}>
              <Fade in={true} timeout={500}>
                <Box>
                  {page === 0 && pages[page].icon && (
                    <Box mb={3}>{pages[page].icon}</Box>
                  )}

                  <Typography
                    variant="h4"
                    style={{
                      fontFamily: fontFamily.primary,
                      fontWeight: 700,
                      fontSize: "1.4rem",
                      marginBottom: 20,
                      color: color.NETURALS900,
                      lineHeight: 1.3,
                      letterSpacing: "-0.02em"
                    }}
                  >
                    {pages[page].title}
                  </Typography>

                  {pages[page].content && (
                    <Typography
                      variant="body1"
                      style={{
                        maxWidth: "600px",
                        margin: "0 auto",
                        lineHeight: 1.6,
                        color: color.LIGHTGRAY,
                        fontFamily: fontFamily.primary,
                        fontSize: "0.95rem"
                      }}
                    >
                      {pages[page].content}
                    </Typography>
                  )}
                </Box>
              </Fade>
            </Box>

            <Box display="flex" justifyContent="center" mt={3}>
              {page < 3 ? (
                <>
                  {page > 0 && (
                    <Button
                      variant="outlined"
                      onClick={() => setPage(page - 1)}
                      style={{
                        minWidth: 160,
                        height: 48,
                        borderRadius: 24,
                        fontFamily: fontFamily.primary,
                        fontWeight: 600,
                        fontSize: fontSize.normal,
                        textTransform: "none",
                        borderColor: color.BLUE,
                        color: color.BLUE,
                        borderWidth: 2,
                        marginRight: 16
                      }}
                      startIcon={<ArrowBackIcon />}
                    >
                      Previous
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setPage(page + 1)}
                    style={{
                      minWidth: 160,
                      height: 48,
                      borderRadius: 24,
                      fontFamily: fontFamily.primary,
                      fontWeight: 700,
                      fontSize: fontSize.normal,
                      textTransform: "none",
                      background: `linear-gradient(135deg, ${color.BLUE} 0%, ${color.DARKBLUE} 100%)`,
                      boxShadow: "0 6px 20px rgba(3, 125, 186, 0.4)"
                    }}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Next
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => onConsent(false)}
                    style={{
                      minWidth: 180,
                      height: 52,
                      borderRadius: 26,
                      fontFamily: fontFamily.primary,
                      fontWeight: 600,
                      fontSize: fontSize.medium,
                      textTransform: "none",
                      borderColor: color.LIGHTGRAY,
                      color: color.LIGHTGRAY,
                      borderWidth: 2,
                      marginRight: 16
                    }}
                  >
                    No, Thank You
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onConsent(true)}
                    style={{
                      minWidth: 180,
                      height: 52,
                      borderRadius: 26,
                      fontFamily: fontFamily.primary,
                      fontWeight: 700,
                      fontSize: fontSize.medium,
                      textTransform: "none"
                    }}
                  >
                    Yes, I Consent
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
